import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { FormattedMessage, useIntl } from "react-intl";
import { useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { useReportStaysApi } from "./infrastructure/reportStays.api";
import {
  generateReportStaysCsv,
  generateReportStaysHeaders,
  generateReportStaysRequest,
  useReportStaysFilters,
} from "./application/reportStays.service";
import React, { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { currentDate } from "../../lib/dateUtils";
import CrudList, { defaultListMessages } from "../shared/crud/list";
import { ReportStaysEntity } from "./domain/reportStays.entity";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { reportStaysMessages } from "./messages";
import { useProperty } from "../../components/property-provider";
import { TableRowActionProps } from "../shared/actions";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt } from "@fortawesome/free-regular-svg-icons";
import { useHistory } from "react-router";
import { useLocaleProvider } from "../../components/locale-provider";

const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZES = [100, 500, 1000];
const TOOLTIP_CONFIG = {
  delay: { show: 250, hide: 0 },
};

function ReportStaysPerDay({ url }: { url: string }) {
  const intl = useIntl();
  const history = useHistory();
  const configuration = useConfig();
  const { currentLocale } = useLocaleProvider();
  const apiClient = useHttpClient(configuration.apiEndpoint);
  const { getStaysPerDayReport } = useReportStaysApi(apiClient);
  const property = useProperty();
  const filters = useReportStaysFilters();
  const [showComments, setShowComments] = useState<boolean>(false);
  const [totalResults, setTotalResults] = useState<number>(0);

  const headers = useMemo(() =>
    generateReportStaysHeaders(showComments),
    [generateReportStaysHeaders, showComments]
  );
  const lastSlashIndex = useMemo(() => url.lastIndexOf("/"), [url]);
  const urlNoTicker = useMemo(() => url.substring(0, lastSlashIndex), [url, lastSlashIndex]);

  const fetchStaysPerDayReport = useCallback(async ({ currentPage, filterValues, pageSize, setTotalRowsCount }) => {
    const reportStaysRequest = generateReportStaysRequest({
      defaultPropertyTicker: property.info.ticker,
      currentPage,
      filterValues,
      pageSize,
    });
    setShowComments(Boolean(reportStaysRequest.request.showComments));
    const response = await getStaysPerDayReport({
      ...reportStaysRequest,
      sumText: intl.formatMessage(reportStaysMessages.totalSum),
      avgText: intl.formatMessage(reportStaysMessages.average),
    });
    setTotalResults(response.totalResults);
    if (typeof setTotalRowsCount === "function") {
      setTotalRowsCount(response.totalResults);
    }
    return response.report.map(it => ({
      ...it,
      action: reportStaysMessages[it.action] ? intl.formatMessage(reportStaysMessages[it.action]) : "",
      dayOfTheWeek: reportStaysMessages[it.dayOfTheWeek.toLowerCase()]
        ? intl.formatMessage(reportStaysMessages[it.dayOfTheWeek.toLowerCase()])
        : it.dayOfTheWeek,
    }));
  }, [getStaysPerDayReport, intl]);

  const onExport = useCallback((async ({ currentPage, filterValues, pageSize }) => {
    if (!totalResults || !currentLocale) {
      return;
    }

    const results = await fetchStaysPerDayReport({
      currentPage: -1,
      filterValues,
      pageSize: totalResults,
      setTotalRowsCount: undefined
    });
    if (!results) {
      return;
    }

    const link = document.createElement("a");
    link.href = generateReportStaysCsv(results, currentLocale, showComments);
    link.download = `stays_per_day_report_${format(currentDate(), "yyyyMMdd")}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }), [fetchStaysPerDayReport, totalResults, currentLocale]);

  const handleDetail = useCallback(
    ({ propertyTicker, bookingId }: any) => {
      history.push(`${urlNoTicker}/${propertyTicker}/reservation-detail/${bookingId}`);
    },
    [history, urlNoTicker]
  );

  const renderTableRowActions = useCallback((props: TableRowActionProps<any>) => {
    return (
      <Table.Cell {...props.rest}>
        <OverlayTrigger {...TOOLTIP_CONFIG} overlay={oProps => (
          <Tooltip id="detail-tooltip" {...oProps}>
            <FormattedMessage {...reportStaysMessages.detail} />
          </Tooltip>
        )}>
          <Button
            disabled={false}
            size="sm"
            className="mr-2"
            variant="light"
            onClick={() => handleDetail(props.row)}
          >
            <FontAwesomeIcon icon={faFileAlt} />
          </Button>
        </OverlayTrigger>
      </Table.Cell>
    );
  }, [handleDetail]);

  if (!filters || !Object.keys(filters).length) {
    return null;
  }

  return (
    <CrudList<ReportStaysEntity, {}>
      messages={{
        ...defaultListMessages,
        entityName: reportStaysMessages.title,
        tableHeaders: reportStaysMessages,
      }}
      rowActions={props => renderTableRowActions(props)}
      columns={headers}
      onGetAll={fetchStaysPerDayReport}
      onExport={onExport}
      paging
      pageSizes={PAGE_SIZES}
      defaultPageSize={DEFAULT_PAGE_SIZE}
      filters={filters}
      managerName={"staysPerDayReport"}
      fixedActions={true}
    />
  )
}

export default withAuthorizationVerifier(ReportStaysPerDay);
